import * as React from "react"
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md"
import { Box, Flex, IconButton, useBreakpointValue } from "@chakra-ui/react"
import useEmblaCarousel from "embla-carousel-react"

import { PostItemFragment } from "lib/graphql"

import { FeedPostTile } from "./FeedPostTile"

interface Props {
  posts: PostItemFragment[]
  postsCount: number
}

export function RecommendedPostsSlider({ posts, postsCount }: Props) {
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const displayCount = isMobile ? 1 : 2

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: displayCount,
    duration: 30,
  })

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setCurrentIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
      setCurrentIndex(0)
    }
  }, [isMobile, emblaApi])

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const canScrollPrev = currentIndex > 0
  const canScrollNext = currentIndex + displayCount < postsCount

  return (
    <>
      <Box id="my-embla-carousel-recommended-posts" position="relative">
        <Box className="embla" ref={emblaRef} overflow="hidden">
          <Box className="embla__container" display="flex">
            {posts.map((post) => (
              <Box
                key={post.id}
                cursor="pointer"
                className="embla__slide"
                flex={`0 0 calc(${100 / displayCount}% - ${displayCount > 1 ? 15 : 0}px)`}
                minW={0}
                m={2}
                mr={displayCount > 1 ? "30px" : 0}
                aria-label={`Recommended post tile for ${post.title}`}
              >
                <FeedPostTile post={post} isRecommended />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {postsCount > displayCount && (
        <Flex justifyContent="space-between" w="100%" pt={2}>
          <IconButton
            colorScheme="gray"
            variant="ghost"
            aria-label="vorige dia"
            icon={<MdKeyboardArrowLeft size={48} />}
            onClick={(e) => {
              e.stopPropagation()
              scrollPrev()
            }}
            disabled={!canScrollPrev}
            width="3rem"
          />
          <IconButton
            colorScheme="gray"
            variant="ghost"
            aria-label="volgende dia"
            icon={<MdKeyboardArrowRight size={48} />}
            onClick={(e) => {
              e.stopPropagation()
              scrollNext()
            }}
            disabled={!canScrollNext}
            width="3rem"
            ml={5}
          />
        </Flex>
      )}
    </>
  )
}
